import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Button, Badge } from 'react-bootstrap';
import axios from 'axios';
import MasterLayout from '../../masterLayout/MasterLayout';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import StatCard from '../../components/common/StatCard';
import FormModal from '../../components/modals/FormModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import ModernSwitch from '../../components/forms/ModernSwitch';
import CurrencyInput from '../../components/forms/CurrencyInput';
import { API_URL } from '../../config/api';
import './MinimumWithdrawalPlansPage.scss';

const MinimumWithdrawalPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({});
  
  const [formData, setFormData] = useState({
    plan_code: '',
    plan_name: '',
    plan_description: '',
    minimum_amount_cents: 10000, // R$ 100,00 padrão
    is_active: true
  });

  // Carregar planos
  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/admin/minimum-withdrawal-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPlans(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      alert('Erro ao carregar planos de valor mínimo');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingPlan(null);
    setFormData({
      plan_code: '',
      plan_name: '',
      plan_description: '',
      minimum_amount_cents: 10000,
      is_active: true
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (plan) => {
    setEditingPlan(plan);
    setFormData({
      plan_code: plan.plan_code,
      plan_name: plan.plan_name,
      plan_description: plan.plan_description || '',
      minimum_amount_cents: plan.minimum_amount_cents,
      is_active: plan.is_active
    });
    setShowModal(true);
  };

  const handleSavePlan = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!formData.plan_code || !formData.plan_name) {
        alert('Preencha todos os campos obrigatórios');
        return;
      }

      if (formData.minimum_amount_cents < 0) {
        alert('Valor mínimo não pode ser negativo');
        return;
      }

      const payload = {
        plan_code: formData.plan_code,
        plan_name: formData.plan_name,
        plan_description: formData.plan_description,
        minimum_amount_cents: formData.minimum_amount_cents,
        is_active: formData.is_active
      };

      if (editingPlan) {
        await axios.put(
          `${API_URL}/api/admin/minimum-withdrawal-plans/${editingPlan.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/admin/minimum-withdrawal-plans`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setShowModal(false);
      loadPlans();
    } catch (error) {
      console.error('Erro ao salvar plano:', error);
      alert(error.response?.data?.error || 'Erro ao salvar plano');
    }
  };

  const handleSetDefault = (plan) => {
    setConfirmationConfig({
      title: 'Definir como Padrão',
      message: `Deseja definir "${plan.plan_name}" como plano padrão? Todos os novos usuários receberão este plano automaticamente.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${API_URL}/api/admin/minimum-withdrawal-plans/${plan.id}/set-default`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setShowConfirmation(false);
          loadPlans();
        } catch (error) {
          console.error('Erro ao definir plano padrão:', error);
          alert('Erro ao definir plano padrão');
        }
      }
    });
    setShowConfirmation(true);
  };

  const handleDeactivate = (plan) => {
    setConfirmationConfig({
      title: 'Desativar Plano',
      message: `Deseja desativar "${plan.plan_name}"? Usuários com este plano manterão suas configurações, mas não será possível atribuir a novos usuários.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${API_URL}/api/admin/minimum-withdrawal-plans/${plan.id}/deactivate`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setShowConfirmation(false);
          loadPlans();
        } catch (error) {
          console.error('Erro ao desativar plano:', error);
          alert(error.response?.data?.error || 'Erro ao desativar plano');
        }
      }
    });
    setShowConfirmation(true);
  };

  return (
    <MasterLayout>
      <div className="dashboard-main-body">
        <div className="container-fluid p-4">
          <AdminPageHeader
            icon="mdi:cash-check"
            title="Valor Mínimo de Saque"
            subtitle="Gerencie os valores mínimos que podem ser atribuídos aos produtores para solicitação de saque"
          />

          <div className="minimum-withdrawal-plans-page">
            {/* Estatísticas */}
            <div className="row g-3" style={{ marginBottom: '24px' }}>
              <div className="col-xxl-4 col-sm-6">
                <StatCard
                  variant="blue"
                  title="Total de Planos"
                  value={plans.length}
                  change="Planos cadastrados"
                  icon="mdi:package-variant"
                />
              </div>
              <div className="col-xxl-4 col-sm-6">
                <StatCard
                  variant="green"
                  title="Planos Ativos"
                  value={plans.filter(p => p.is_active).length}
                  change="Planos disponíveis"
                  icon="mdi:check-circle"
                />
              </div>
              <div className="col-xxl-4 col-sm-6">
                <StatCard
                  variant="purple"
                  title="Plano Padrão"
                  value={plans.find(p => p.is_default)?.plan_name || 'N/A'}
                  change="Plano padrão do sistema"
                  icon="mdi:star"
                />
              </div>
            </div>

            {/* Botão Novo Plano */}
            <div className="d-flex justify-content-end mb-3">
              <Button variant="primary" onClick={handleOpenCreateModal}>
                <Icon icon="mdi:plus" className="me-2" />
                Novo Plano
              </Button>
            </div>

            {/* Lista de Planos em Cards */}
            <div className="minimum-withdrawal-plans-page__content">
              {loading ? (
                <div className="loading-state">
                  <span className="loading-state__spinner">⏳</span>
                  <span className="loading-state__text">Carregando planos...</span>
                </div>
              ) : plans.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-state__icon">📭</span>
                  <span className="empty-state__text">Nenhum plano cadastrado.</span>
                </div>
              ) : (
                <div className="plans-list">
                  {plans.map((plan) => (
                    <div key={plan.id} className="plan-card">
                      <div className="plan-card__avatar">
                        <span className="plan-card__avatar-text">
                          {plan.plan_code?.charAt(0)?.toUpperCase() || 'P'}
                        </span>
                      </div>
                      
                      <div className="plan-card__info">
                        <div className="plan-card__name">
                          {plan.plan_name}
                          {plan.is_default && (
                            <Badge bg="warning" className="ms-2">
                              ⭐ Padrão
                            </Badge>
                          )}
                        </div>
                        <div className="plan-card__code">
                          Código: <code>{plan.plan_code}</code>
                        </div>
                        {plan.plan_description && (
                          <div className="plan-card__description">
                            {plan.plan_description}
                          </div>
                        )}
                      </div>

                      <div className="plan-card__stats">
                        <div className="plan-card__stat">
                          <span className="plan-card__stat-value">
                            R$ {(plan.minimum_amount_cents / 100).toFixed(2)}
                          </span>
                          <span className="plan-card__stat-label">Valor Mínimo</span>
                        </div>
                        <div className="plan-card__stat">
                          <span className="plan-card__stat-value">
                            {plan.is_active ? (
                              <Badge bg="success">Ativo</Badge>
                            ) : (
                              <Badge bg="secondary">Inativo</Badge>
                            )}
                          </span>
                          <span className="plan-card__stat-label">Status</span>
                        </div>
                      </div>

                      <div className="plan-card__actions">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenEditModal(plan)}
                        >
                          <Icon icon="mdi:pencil" className="me-1" />
                          Editar
                        </Button>
                        {!plan.is_default && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleSetDefault(plan)}
                          >
                            <Icon icon="mdi:star" className="me-1" />
                            Padrão
                          </Button>
                        )}
                        {!plan.is_default && plan.is_active && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeactivate(plan)}
                          >
                            <Icon icon="mdi:close" className="me-1" />
                            Desativar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Criação/Edição */}
      <FormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={editingPlan ? 'Editar Plano' : 'Novo Plano'}
        onSave={handleSavePlan}
        size="lg"
      >
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Código do Plano *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: MIN_100, MIN_200"
              value={formData.plan_code}
              onChange={(e) => setFormData({ ...formData, plan_code: e.target.value.toUpperCase() })}
              disabled={editingPlan !== null}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Nome do Plano *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ex: Padrão - R$ 100,00"
              value={formData.plan_name}
              onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
            />
          </div>

          <div className="col-md-12">
            <label className="form-label">Descrição</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Descrição do plano..."
              value={formData.plan_description}
              onChange={(e) => setFormData({ ...formData, plan_description: e.target.value })}
            />
          </div>

          <div className="col-md-12">
            <label className="form-label">Valor Mínimo (R$) *</label>
            <CurrencyInput
              value={formData.minimum_amount_cents / 100}
              onChange={(value) => setFormData({ ...formData, minimum_amount_cents: Math.round(value * 100) })}
              placeholder="R$ 0,00"
            />
            <small className="text-muted">Valor mínimo para solicitação de saque</small>
          </div>

          <div className="col-md-12">
            <ModernSwitch
              label="Plano Ativo"
              checked={formData.is_active}
              onChange={(value) => setFormData({ ...formData, is_active: value })}
              description="Apenas planos ativos podem ser atribuídos a novos usuários"
            />
          </div>
        </div>
      </FormModal>

      {/* Modal de Confirmação */}
      <ConfirmationModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        onConfirm={confirmationConfig.onConfirm}
      />
    </MasterLayout>
  );
};

export default MinimumWithdrawalPlansPage;

